import { CSSProperties, FC, useState } from 'react';
import InterviewResultModal from './InterviewResultModal';

// ---------------------------------------------------------------------------
// Props contract — self-contained and reusable. The badge depends on nothing
// specific to the Completion tab so it can later be embedded in job-application
// cards. Its only inputs are the three props below.
// ---------------------------------------------------------------------------

export interface ConsoleInterviewBadgeProps {
  recommendation: string | null | undefined;
  score: number | null | undefined;
  /** Required only when interactive; the modal needs it to load the result. */
  pipelineEntryId?: string | null | undefined;
  /** Optional candidate name passed through to the modal header. */
  candidateName?: string | null | undefined;
  /**
   * When true (default) the pill is a button that opens the result modal.
   * When false it renders as a plain, non-interactive <span> — used on
   * job-application cards that don't load the console stylesheet.
   */
  interactive?: boolean;
}

// ---------------------------------------------------------------------------
// Verdict tones — derived from the recommendation's leading verb.
//   advance -> positive (green) / review -> neutral (amber) / reject -> danger
// ---------------------------------------------------------------------------

type VerdictTone = 'positive' | 'neutral' | 'danger';

interface VerdictDisplay {
  tone: VerdictTone;
  label: string;
}

const VERDICT_DISPLAY: Record<string, VerdictDisplay> = {
  advance: { tone: 'positive', label: 'Advance' },
  review: { tone: 'neutral', label: 'Review' },
  reject: { tone: 'danger', label: 'Reject' },
};

// Inline tone colors so the pill renders correctly OUTSIDE the console
// stylesheet (job-application cards don't import ai-recruiter-console.scss).
const TONE_COLORS: Record<VerdictTone, { background: string; color: string }> = {
  positive: { background: '#D1FAE5', color: '#066E52' },
  neutral: { background: '#FDECCC', color: '#B5760F' },
  danger: { background: '#FDECEE', color: '#B71C1C' },
};

// Base pill layout so it looks right without the scss.
const PILL_LAYOUT_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: 6,
  padding: '1px 7px',
  borderRadius: 10,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  lineHeight: 1.4,
};

// Parse the leading verb from the raw text. The model returns e.g.
// "advance, strong communication..." — so the first token carries a trailing
// comma. Match only the leading alphabetic run so "advance," -> "advance".
const parseVerdict = (recommendation: string): VerdictDisplay => {
  const firstWord = recommendation.trim().toLowerCase().match(/^[a-z]+/)?.[0] ?? '';
  return VERDICT_DISPLAY[firstWord] ?? { tone: 'neutral', label: 'Review' };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConsoleInterviewBadge: FC<ConsoleInterviewBadgeProps> = ({
  recommendation,
  score,
  pipelineEntryId,
  candidateName,
  interactive = true,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  // Nothing to show when there is no interview result.
  if (!recommendation || recommendation.trim() === '') {
    return null;
  }

  const verdict = parseVerdict(recommendation);
  const label =
    score != null ? `${verdict.label} · ${score}` : verdict.label;
  const pillStyle: CSSProperties = { ...PILL_LAYOUT_STYLE, ...TONE_COLORS[verdict.tone] };

  // Non-interactive: a plain span with no modal / onClick. Used on
  // job-application cards outside the console.
  if (!interactive) {
    return (
      <span
        className={`ai-console__interview-badge ai-console__interview-badge--${verdict.tone}`}
        style={pillStyle}
      >
        {label}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`ai-console__interview-badge ai-console__interview-badge--${verdict.tone}`}
        style={pillStyle}
        onClick={() => setModalOpen(true)}
        title="View AI interview result"
      >
        {label}
      </button>

      {pipelineEntryId ? (
        <InterviewResultModal
          isOpen={modalOpen}
          pipelineEntryId={pipelineEntryId}
          candidateName={candidateName}
          onClose={() => setModalOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ConsoleInterviewBadge;
